import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  AlertTriangle, MessageSquare, TrendingUp, Package, Briefcase, 
  Bitcoin, Heart, ArrowRight, Shield, ChevronDown, ChevronUp, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const scamArticles = [
  {
    id: 'whatsapp-impersonation',
    icon: MessageSquare,
    title: 'WhatsApp Impersonation Scams',
    summary: 'Criminals posing as family members or friends requesting urgent money transfers.',
    color: 'green',
    content: {
      howItWorks: `WhatsApp impersonation scams are one of the most common fraud types in the UK. The scammer will message you pretending to be a family member, typically a son or daughter, claiming they have a new phone number.

The message often starts with "Hi Mum" or "Hi Dad" and explains that their old phone is broken or lost. After building rapport through a few messages, they will ask for urgent financial help - often claiming they need to pay a bill, are stuck somewhere, or have an emergency.

The scammer will provide bank details that don't match your family member's usual account. They rely on the emotional urgency and the victim's desire to help their loved one.`,
      warningSigns: [
        'Unsolicited message from an unknown number claiming to be a family member',
        'Urgency and pressure to send money quickly',
        'Requests for bank transfers rather than other payment methods',
        'Different bank account details than your family member normally uses',
        'Reluctance to speak on a phone call or video call',
        'Stories that prevent verification (broken phone, can\'t access old account)',
      ],
      howToAvoid: [
        'Always verify by calling the person on their known number or meeting them',
        'Ask a question only your real family member would know',
        'Never send money based solely on a text message',
        'Be suspicious of any urgent requests for money',
        'Tell the person you\'ll call them back on their original number',
        'Discuss this scam with elderly relatives who may be more vulnerable',
      ],
    },
  },
  {
    id: 'fake-investment',
    icon: TrendingUp,
    title: 'Fake Investment Platform Scams',
    summary: 'Fraudulent websites promising guaranteed high returns on investments.',
    color: 'blue',
    content: {
      howItWorks: `Fake investment platform scams lure victims with promises of guaranteed high returns, often through professional-looking websites and social media advertising. These platforms may mimic legitimate investment services.

The scammer typically contacts victims via social media ads, dating apps, or cold calls. They direct victims to a sophisticated-looking trading platform where initial small investments appear to show impressive gains.

Victims are encouraged to invest more, often taking out loans or using savings. When they try to withdraw funds, they face endless excuses, additional fees, or the platform simply disappears. The "profits" shown on screen were never real.`,
      warningSigns: [
        'Promises of guaranteed returns or unusually high profits',
        'Pressure to invest quickly before an "opportunity" expires',
        'Unregulated platforms not registered with the FCA',
        'Cold calls or unsolicited contact about investments',
        'Celebrity endorsements (often fake) promoting the platform',
        'Difficulty or inability to withdraw your money',
        'Requests to pay "fees" or "taxes" before withdrawal',
      ],
      howToAvoid: [
        'Check the FCA register before investing (https://register.fca.org.uk)',
        'Be extremely skeptical of guaranteed returns',
        'Never invest based on social media ads or cold calls',
        'Research the company thoroughly before investing',
        'Start with small amounts you can afford to lose',
        'If it sounds too good to be true, it probably is',
        'Consult a regulated financial advisor for investment advice',
      ],
    },
  },
  {
    id: 'parcel-delivery',
    icon: Package,
    title: 'Parcel Delivery Phishing Scams',
    summary: 'Fake delivery notifications designed to steal your personal and financial information.',
    color: 'orange',
    content: {
      howItWorks: `Parcel delivery phishing scams exploit the high volume of online shopping by sending fake delivery notifications via text message or email. The message claims a package couldn't be delivered and asks you to click a link.

The link leads to a fake website mimicking Royal Mail, DPD, Evri, or other carriers. The site requests a small "redelivery fee" (usually £1-3) and asks for your card details. Once entered, scammers have your full payment information.

Some variants install malware on your device or lead to fake login pages that steal your credentials. The scammers then use your details for fraud or sell them to other criminals.`,
      warningSigns: [
        'Unexpected delivery notification when you\'re not expecting a package',
        'Generic messages without specific package or order details',
        'Links to websites with slightly misspelled domain names',
        'Requests for payment to release or redeliver a package',
        'Urgency suggesting the package will be returned if you don\'t act quickly',
        'Poor spelling or grammar in the message',
        'Sender email address doesn\'t match the legitimate company',
      ],
      howToAvoid: [
        'Don\'t click links in unexpected delivery messages',
        'Go directly to the carrier\'s official website to check tracking',
        'Verify the sender\'s email address carefully',
        'Legitimate carriers don\'t request card details via text',
        'Check your recent orders to see if you\'re expecting anything',
        'Report suspicious messages to the carrier and 7726 (spam reporting)',
        'Keep your browser and security software updated',
      ],
    },
  },
  {
    id: 'job-scam',
    icon: Briefcase,
    title: 'Fake Job Offer Scams',
    summary: 'Fraudulent employment opportunities designed to steal money or personal information.',
    color: 'yellow',
    content: {
      howItWorks: `Fake job scams target job seekers with attractive but fraudulent employment offers. Scammers post fake jobs on legitimate platforms or contact victims directly via email, LinkedIn, or WhatsApp.

Common variants include work-from-home opportunities, "money mule" jobs (transferring money between accounts), or roles requiring upfront payment for equipment, training, or background checks. Some scammers conduct fake interviews to collect personal information.

Victims may lose money paid for fake equipment or training, have their identity stolen, or unknowingly become involved in money laundering. The "employer" eventually disappears or the promised job never materialises.`,
      warningSigns: [
        'Job offers that arrive without you applying',
        'Unusually high salary for minimal qualifications',
        'Requests for upfront payments for equipment, training, or checks',
        'Vague job descriptions with no clear responsibilities',
        'Interviews conducted only via text message or messaging apps',
        'Pressure to start immediately without proper contracts',
        'Job involves transferring money or reshipping packages',
        'Company has no verifiable online presence or reviews',
      ],
      howToAvoid: [
        'Research the company thoroughly before applying or accepting',
        'Never pay money to get a job',
        'Be wary of jobs offering high pay for easy work',
        'Verify contact details independently, not through the job posting',
        'Insist on proper employment contracts before starting',
        'Don\'t provide bank details until employment is verified',
        'Report suspicious job postings to the platform',
        'Trust your instincts if something feels wrong',
      ],
    },
  },
  {
    id: 'crypto-scam',
    icon: Bitcoin,
    title: 'Cryptocurrency Trading Scams',
    summary: 'Fraudulent crypto investment schemes and fake trading platforms.',
    color: 'purple',
    content: {
      howItWorks: `Cryptocurrency scams exploit the complexity and hype around digital currencies. Fraudsters create fake trading platforms, Ponzi schemes, or "pump and dump" schemes to steal money from victims.

Common methods include fake crypto exchanges showing impressive fake profits, romance scams where the "partner" introduces you to a crypto investment, and social media gurus promising trading secrets. Scammers may also impersonate legitimate crypto companies.

Victims are shown fake dashboards with growing "investments." When they try to withdraw, they're told to pay taxes, fees, or make additional deposits. Eventually, the platform closes or becomes inaccessible, and the money is gone.`,
      warningSigns: [
        'Guaranteed profits or risk-free returns on crypto',
        'Pressure to invest quickly in a "limited opportunity"',
        'Unregulated platforms not registered with financial authorities',
        'Romantic interests introducing you to crypto investments',
        'Celebrity endorsements (usually fake) promoting crypto schemes',
        'Requests for additional payments to withdraw your funds',
        'Can\'t find independent reviews of the platform',
        'Being asked to download unfamiliar wallet apps',
      ],
      howToAvoid: [
        'Only use well-known, regulated cryptocurrency exchanges',
        'Never invest based on someone you met online',
        'Be extremely skeptical of guaranteed crypto returns',
        'Research any platform independently before investing',
        'Don\'t let anyone else manage your crypto wallet',
        'Keep private keys and seed phrases completely private',
        'Start small with amounts you can afford to lose',
        'Remember that legitimate investments carry risk',
      ],
    },
  },
  {
    id: 'romance-scam',
    icon: Heart,
    title: 'Romance Scams',
    summary: 'Criminals building fake romantic relationships to manipulate victims into sending money.',
    color: 'red',
    content: {
      howItWorks: `Romance scams involve criminals creating fake profiles on dating sites, social media, or apps to build emotional relationships with victims. The scammer invests weeks or months building trust before asking for money.

The scammer typically claims to be overseas (military, oil rig worker, international business) which explains why they can't meet in person or video call. They profess love quickly and build an intense emotional connection.

Eventually, crises emerge requiring money: medical emergencies, legal problems, travel costs to visit you, or business issues. Victims send money believing they're helping someone they love. The requests continue until the victim runs out of money or becomes suspicious.`,
      warningSigns: [
        'Quick declarations of love or intense emotional connection',
        'Person claims to be overseas and can\'t meet in person',
        'Avoids video calls or makes excuses when asked',
        'Profile photos look too polished or professional',
        'Stories become increasingly dramatic with financial emergencies',
        'Requests for money via wire transfer, gift cards, or crypto',
        'Gets angry or emotional when questioned',
        'Relationship exists only online despite passing time',
      ],
      howToAvoid: [
        'Be cautious of profiles that seem too perfect',
        'Do a reverse image search on their photos',
        'Never send money to someone you haven\'t met in person',
        'Insist on video calls early in the relationship',
        'Be suspicious of people who always have excuses not to meet',
        'Tell friends and family about online relationships',
        'Trust your instincts if something feels off',
        'Remember that real love doesn\'t require money transfers',
      ],
    },
  },
];

const ScamLibrary = () => {
  const [expandedArticle, setExpandedArticle] = useState(null);

  const toggleArticle = (id) => {
    setExpandedArticle(expandedArticle === id ? null : id);
  };

  const getColorClasses = (color) => {
    const colors = {
      green: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-500', icon: 'text-green-500' },
      blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-500', icon: 'text-blue-500' },
      orange: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-500', icon: 'text-orange-500' },
      yellow: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-500', icon: 'text-yellow-500' },
      purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-500', icon: 'text-purple-500' },
      red: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-500', icon: 'text-red-500' },
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <Helmet>
        <title>Scam Library | Learn About Common Scams | ActionFraudOne</title>
        <meta name="description" content="Free educational resources about common scams including WhatsApp impersonation, fake investments, crypto fraud, romance scams, and more." />
      </Helmet>

      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/30 rounded-full mb-6">
            <AlertTriangle className="w-4 h-4 text-gold" />
            <span className="text-gold text-sm font-medium">Free Educational Resource</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Scam Awareness Library
          </h1>
          <p className="text-gray-400 text-lg">
            Learn how common scams work, recognise the warning signs, and protect yourself from fraud.
          </p>
        </div>

        {/* Articles */}
        <div className="max-w-4xl mx-auto space-y-4">
          {scamArticles.map((article) => {
            const colors = getColorClasses(article.color);
            const isExpanded = expandedArticle === article.id;
            const Icon = article.icon;
            
            return (
              <Card key={article.id} className={`${colors.bg} ${colors.border} transition-all`}>
                <CardContent className="p-0">
                  {/* Header */}
                  <button
                    onClick={() => toggleArticle(article.id)}
                    className="w-full p-6 flex items-center justify-between text-left"
                    data-testid={`scam-article-${article.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 ${colors.bg} rounded-lg flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 ${colors.icon}`} />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white">{article.title}</h2>
                        <p className="text-gray-400 text-sm mt-1">{article.summary}</p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-6 h-6 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  
                  {/* Content */}
                  {isExpanded && (
                    <div className="px-6 pb-6 border-t border-white/10">
                      {/* How It Works */}
                      <div className="mt-6">
                        <h3 className={`text-lg font-bold ${colors.text} mb-3`}>How This Scam Works</h3>
                        <div className="text-gray-300 leading-relaxed whitespace-pre-line">
                          {article.content.howItWorks}
                        </div>
                      </div>
                      
                      {/* Warning Signs */}
                      <div className="mt-6">
                        <h3 className={`text-lg font-bold ${colors.text} mb-3`}>Warning Signs</h3>
                        <ul className="space-y-2">
                          {article.content.warningSigns.map((sign, i) => (
                            <li key={i} className="flex items-start gap-3 text-gray-300">
                              <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-1" />
                              <span>{sign}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* How to Avoid */}
                      <div className="mt-6">
                        <h3 className={`text-lg font-bold ${colors.text} mb-3`}>How to Protect Yourself</h3>
                        <ul className="space-y-2">
                          {article.content.howToAvoid.map((tip, i) => (
                            <li key={i} className="flex items-start gap-3 text-gray-300">
                              <Shield className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* CTA */}
                      <div className="mt-8 p-4 bg-gold/10 border border-gold/20 rounded-lg">
                        <p className="text-gray-300 mb-4">
                          <strong className="text-gold">Not sure if you've received a scam?</strong> Use our Scam Check service 
                          to verify suspicious messages or offers.
                        </p>
                        <Link to="/scam-check">
                          <Button className="bg-gold hover:bg-gold/90 text-black font-bold">
                            <Search className="mr-2" size={16} />
                            Check a Suspicious Message
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="max-w-3xl mx-auto mt-16 text-center">
          <Card className="bg-gradient-to-r from-[#1e40af]/10 to-transparent border-[#1e40af]/30">
            <CardContent className="p-8">
              <Shield className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-4">Think You've Spotted a Scam?</h2>
              <p className="text-gray-400 mb-6">
                Our Scam Check service helps you verify suspicious messages, websites, and investment offers. 
                Get a professional risk assessment from just £4.99.
              </p>
              <Link to="/scam-check">
                <Button size="lg" className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white font-bold">
                  Get Your Scam Check <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ScamLibrary;
